import './coapplicantPreviousResidencePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/AddressLineOne/AddressLineOne';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const {
    coapplicantPreviousResidenceCity,
    coapplicantPreviousResidenceLineOne,
    coapplicantPreviousResidenceLineTwo,
    coapplicantPreviousResidencePostalCode,
    coapplicantPreviousResidenceState,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantPreviousResidenceCity,
    setCoapplicantPreviousResidenceLineOne,
    setCoapplicantPreviousResidenceLineTwo,
    setCoapplicantPreviousResidencePostalCode,
    setCoapplicantPreviousResidenceState,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      cityValue: coapplicantPreviousResidenceCity,
      lineOneValue: coapplicantPreviousResidenceLineOne,
      lineTwoValue: coapplicantPreviousResidenceLineTwo,
      postalCodeValue: coapplicantPreviousResidencePostalCode,
      stateValue: coapplicantPreviousResidenceState,
    },
    delayError: 200,
    mode: 'onBlur',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      cityValue,
      lineOneValue,
      lineTwoValue,
      stateValue,
      postalCodeValue,
    } = values;

    if (!cityValue || !lineOneValue || !stateValue || !postalCodeValue) {
      return;
    }

    setCoapplicantPreviousResidenceCity(cityValue);
    setCoapplicantPreviousResidenceLineOne(lineOneValue);
    setCoapplicantPreviousResidenceLineTwo(lineTwoValue);
    setCoapplicantPreviousResidencePostalCode(postalCodeValue);
    setCoapplicantPreviousResidenceState(stateValue);

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-previous-residence-page"
        sx={theme.pageLayout}
      >
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantPreviousResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantPreviousResidence.form.h1}
              >
                {t('CoapplicantPreviousResidencePage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantPreviousResidence.form.h2}
              >
                {t('CoapplicantPreviousResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantPreviousResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    errorTestId={
                      locator.pages.coapplicantPreviousResidence.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    label={t(
                      'CoapplicantPreviousResidencePage.residenceLineOne',
                    )}
                    name="lineOneValue"
                    register={register}
                    setValue={setValue}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantPreviousResidence.form
                            .addressLine1Input,
                      },
                    }}
                    suggestionFields={{
                      cityFieldName: 'cityValue',
                      stateFieldName: 'stateValue',
                      postalCodeFieldName: 'postalCodeValue',
                    }}
                    validation={addressValidation}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('lineTwoValue')}
                        autoComplete="address-line2"
                        label={t(
                          'CoapplicantPreviousResidencePage.residenceLineTwo',
                        )}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantPreviousResidence.form
                                .addressLine2Input,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('cityValue', cityValidation)}
                        autoComplete="address-level2"
                        error={!isEmpty(errors.cityValue)}
                        label={t(
                          'CoapplicantPreviousResidencePage.residenceCity',
                        )}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('cityValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantPreviousResidence.form
                                .cityInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.cityValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantPreviousResidence.form
                              .cityInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.cityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Select
                        control={control}
                        errorTestId={
                          locator.pages.coapplicantPreviousResidence.form
                            .stateSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantPreviousResidence.form
                            .stateSelect
                        }
                        label={t(
                          'CoapplicantPreviousResidencePage.residenceState',
                        )}
                        name="stateValue"
                        options={USStates.map(({ abbreviation, name }) => ({
                          name,
                          value: abbreviation,
                        }))}
                        rules={stateValidation}
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('postalCodeValue', postalCodeValidation)}
                        autoComplete="postal-code"
                        error={!isEmpty(errors.postalCodeValue)}
                        label={t('CoapplicantPreviousResidencePage.zip')}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('postalCodeValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantPreviousResidence.form
                                .zipCodeInput,
                            inputMode: 'numeric',
                          },
                        }}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.postalCodeValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantPreviousResidence.form
                              .zipCodeInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.postalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantPreviousResidence.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantPreviousResidencePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantPreviousResidence.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantPreviousResidencePage';
